import React, { ReactNode, useRef, useEffect } from "react";
import { Button, Modal, message } from "antd";
import { useRequest } from "ahooks";
import EwaybillDTO from "@/dto/order/EwaybillDTO";
import OrderDTO from "@/dto/order/OrderDTO";
import { cancel, load } from "@/services/api/ewaybill";
import useUpdateOrderEwaybill from "@/scenes/OrderManagement/hooks/useUpdateOrderEwaybill";
import showCancelErrorDialog from "@/scenes/OrderManagement/utils/showCancelErrorDialog";
import LogisticsCompanyNameMap from "@/map/LogisticsCompanyNameMap";
import copy from "copy-to-clipboard";
import OrderOperationLogModal from "@/scenes/OrderManagement/components/dialogs/OrderOperationLogDialog/OrderOperationLogModal";
import { InjectBaseModalProps } from "@/hooks/Modal/ModalStore";
import { useModalStore } from "@/hooks/Modal/hooks";
import WithOrderItemsInfo from "../WithOrderItemsInfo";
import TrackingButton from "./TrackingButton";
import {
    BackendBlockExpressUrlMap,
    getEwaybillRealLogisticsCompany,
    getEwaybillRealLogisticsCompanyNode,
} from "@/utils/EwaybillUtil";
import CancelEwaybillAlert from "@/components/CancelEwaybillAlert";
import OrderStatus from "@/enum/OrderStatus";
import { shippedOrderCancelEwaybillTip } from "@/utils/dialog";
import { collectEvent } from "@/services/dataTrack";
import DataTrackEvent from "@/services/dataTrack/enum/DataTrackEvent";
import { useGlobalStore } from "@/store/hooks";
import { openUrl } from "@/utils";

interface EwaybillOperateModalProps extends InjectBaseModalProps {
    order: OrderDTO;
    ewaybill: EwaybillDTO;
}

const EwaybillOperateModal: React.FC<EwaybillOperateModalProps> = function (props) {
    const { open, onCancel, order, ewaybill } = props;
    const modalStore = useModalStore();
    const globalStore = useGlobalStore();
    const { runAsync, loading: canceling } = useRequest(cancel, {
        manual: true,
    });

    const { runAsync: loadDetail, loading } = useRequest(load, { manual: true });

    const { handleUpdateOrder } = useUpdateOrderEwaybill();

    const modalRef = useRef<ReturnType<typeof Modal.confirm>>();

    const onOpenOrderLog = () => {
        modalStore.openModal(OrderOperationLogModal, {
            order,
        });

        onCancel();
    };

    const onConfirmOk = async (cancel) => {
        const ewaybills = await loadDetail(order, true);

        const loadEwaybill = ewaybills.find((it) => it.id === ewaybill.id);

        if (!loadEwaybill) {
            Modal.error({ title: "回收电子面单号失败", content: "Cannot load ewaybill detail" });

            cancel?.();

            return;
        }

        const result = await runAsync(order, [loadEwaybill]);

        const { canceledEwaybillIds, errorMap } = result;

        if (!canceledEwaybillIds || canceledEwaybillIds.length === 0) {
            const successfulCount = canceledEwaybillIds?.length || 0;
            const failCount = 1 - successfulCount;

            showCancelErrorDialog({ ewaybills: [ewaybill], errorMap, successfulCount, failCount });
        } else {
            handleUpdateOrder(order, result);

            const shop = globalStore.shopMapping.get(order.shopId);

            if (shop && BackendBlockExpressUrlMap[shop.type]) {
                Modal.success({
                    title: "回收电子面单号成功",
                    content: (
                        <div className="error">
                            如果包裹已被快递揽收，软件内回收单号无法拦截快递，可尝试去后台拦截快递或联系快递拦截
                        </div>
                    ),
                    okCancel: true,
                    okText: "去后台拦截快递",
                    onOk() {
                        openUrl(BackendBlockExpressUrlMap[shop.type]);
                    },
                });
            } else {
                message.success("回收电子面单号成功");
            }

            onCancel();
        }

        try {
            collectEvent(DataTrackEvent.CANCEL_EWAYBILL, {
                count: 1,
            });
        } catch {}
    };

    const handleCancelWaybillNo = async () => {
        const { parentWaybillNo, waybillNo } = ewaybill;

        if (order.status === OrderStatus.SHIPPED) {
            await shippedOrderCancelEwaybillTip();
        }

        if (parentWaybillNo) {
            return Modal.confirm({
                title: "回收该单号吗？",
                content: `${waybillNo}为子单号，回收该单号将会回收对应母单号【${parentWaybillNo}】下面的所有子单号，确认回收该单号吗？`,
                onOk: onConfirmOk,
            });
        }

        return Modal.confirm({
            title: "确定要回收该单号吗？",
            content: <CancelEwaybillAlert />,
            onOk: onConfirmOk,
            width: 790,
        });
    };

    function buildFooter() {
        if (!ewaybill) return null;

        const buttons: ReactNode[] = [];

        const { parentWaybillNo, waybillNo } = ewaybill;

        buttons.push(
            <TrackingButton
                key="track"
                logisticsCompany={getEwaybillRealLogisticsCompany(ewaybill)}
                waybillNo={parentWaybillNo || waybillNo}
                onClickAfter={onCancel}
            >
                跟踪物流
            </TrackingButton>,
        );

        buttons.push(
            <Button onClick={onOpenOrderLog} key="log">
                查看订单操作日志
            </Button>,
        );

        if (!order.preShipped) {
            buttons.push(
                <Button
                    onClick={async () => {
                        try {
                            modalRef.current = await handleCancelWaybillNo();
                        } catch {}
                    }}
                    key="cancel"
                >
                    回收该单号
                </Button>,
            );
        }

        return buttons;
    }

    const handleCopy = (text, e: React.MouseEvent) => {
        e.stopPropagation();

        const logisticsCompany = LogisticsCompanyNameMap[getEwaybillRealLogisticsCompany(ewaybill)];

        copy(logisticsCompany + " " + text);

        message.success("复制成功");
    };

    useEffect(() => {
        const buttonProps = {
            okButtonProps: { loading: canceling || loading },
            cancelButtonProps: { loading: canceling || loading },
        };

        modalRef.current?.update?.(buttonProps);
    }, [canceling, loading]);

    return (
        <Modal width={660} open={open} onCancel={onCancel} mask={{ closable: false }} footer={buildFooter()}>
            <div className="pb10 mb20" style={{ fontSize: "16px", borderBottom: "1px dashed #ccc" }}>
                <span className="mr10"> {getEwaybillRealLogisticsCompanyNode(ewaybill)}</span>
                <span className="mr10">
                    {ewaybill?.waybillNo}{" "}
                    <a
                        style={{ color: "#aaa" }}
                        className="text-small"
                        onClick={handleCopy.bind(null, ewaybill?.waybillNo)}
                    >
                        复制
                    </a>
                </span>
                {ewaybill?.parentWaybillNo && (
                    <span className="mr10">
                        母单号：{ewaybill.parentWaybillNo}{" "}
                        <a
                            style={{ color: "#aaa" }}
                            className="small"
                            onClick={handleCopy.bind(null, ewaybill.parentWaybillNo)}
                        >
                            复制
                        </a>
                    </span>
                )}
            </div>

            <WithOrderItemsInfo order={order} orderItemsInfo={ewaybill.orderItemsInfo} />
        </Modal>
    );
};

export default React.memo(EwaybillOperateModal);
